/**
 * Class representing a slider indicator.
 * Handles updating and toggling the position indicator for a slider component.
 */
class BBDSliderIndicator {
  /**
   * Creates an instance of the slider controller.
   *
   * @param {HTMLElement} sliderElement - The main slider element that emits events.
   * @param {HTMLElement} indicatorElement - The element representing the slide position indicator.
   */
  constructor(sliderElement, indicatorElement) {
    this.sliderElement = sliderElement;
    this.indicatorElement = indicatorElement;
    this.slidesLength = sliderElement.querySelectorAll('slideshow-slide').length;

    this.isSingular = this.sliderElement.closest('.slider--is-singular');

    // Set some initial values
    this.toggleIndicator(0, this.slidesLength);
    this.updateIndicatorPosition(0, this.slidesLength);

    // Init events
    this.initEvents();
  }

  /**
   * Initializes event listeners for the slider.
   *
   * - Listens for the `slideChanged` event.
   * - Updates the indicator visibility and position based on the current slide.
   *
   * @fires slideChanged - Custom event triggered when the slide changes.
   */
  initEvents() {
    this.sliderElement.addEventListener('slideshow:select', (e) => {
      this.toggleIndicator(e.detail.index, this.slidesLength);
      this.updateIndicatorPosition(e.detail.index, this.slidesLength);
    });
  }

  /**
   * Toggles the visibility of the indicator element based on the active slide and total slide count.
   *
   * - Hides the indicator if there is only one slide.
   * - Hides the indicator when the last slide is active.
   * - Keeps the indicator visible otherwise.
   *
   * @param {number} activeIndex - The index of the currently active slide (1-based).
   * @param {number} totalSlides - The total number of slides in the slider.
   */
  toggleIndicator(activeIndex, totalSlides) {
    this.indicatorElement.classList.toggle('hide', activeIndex === totalSlides && totalSlides <= 1);
  }

  /**
   * Updates the position and width of the indicator element based on the active slide.
   *
   * - Calculates the indicator width as a percentage of the total slides.
   * - Determines the offset based on the active slide index.
   * - Updates the CSS custom properties to visually move the indicator.
   *
   * @param {number} activeIndex - The index of the currently active slide (1-based).
   * @param {number} totalSlides - The total number of slides in the slider.
   */
  updateIndicatorPosition(activeIndex, totalSlides) {
    if (this.indicatorElement) {
      // Calculate the width of the indicator as a percentage of the total slides
      let indicatorWidth;

      if (this.isSingular) {
        indicatorWidth = 100 / totalSlides - 1;
      } else {
        indicatorWidth = 100 / totalSlides;
      }

      // Calculate the offset based on the active slide index
      const indicatorOffset = indicatorWidth * activeIndex;

      // Apply calculated values to the CSS custom properties
      this.indicatorElement.style.setProperty('--indicator-width', indicatorWidth + '%');
      this.indicatorElement.style.setProperty('--indicator-offset', indicatorOffset + '%');
    }
  }
}

/**
 * Initializes the BBDSliderIndicator for all slider components on the page.
 */
function initializeSliderIndicators() {
  const componentSliderElements = document.querySelectorAll('slideshow-component');

  componentSliderElements.forEach((element) => {
    const indicatorElement = element.querySelector('[id^="slideshow-indicator"]');

    if (indicatorElement) {
      new BBDSliderIndicator(element, indicatorElement);
    }
  });
}

// Run the initialization function when the DOM is fully loaded
document.addEventListener('DOMContentLoaded', initializeSliderIndicators);
window.initializeSliderIndicators = initializeSliderIndicators;
